import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WithAuth } from "../../components/with-auth";
import MainWrapper from "../../layout/MainWrapper";
import { createAxiosInstance } from "../../api/ApiRequest";
import type { GetProp, TableProps } from 'antd';
import { Input, Table, Spin, Empty } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { SearchOutlined } from "@ant-design/icons";
import { notifyError } from "../../utils/toast";
import { useServerContext } from "../../context/ServerContext";
import { useAuth } from "../../context/AuthContext";
import { resolveTokenLabel } from "../../utils/tokenLabel";

type ColumnsType<T extends object = object> = TableProps<T>['columns'];
type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface DataType {
    _id: string;
    index: number;
    fullname?: string;
    username?: string;
    token_address?: string;
    token_symbol?: string;
    dex?: string;
    pool_id?: string;
    pool_type?: string;
    wallet?: string;
    expire_time?: number;
    createdAt?: number;
}

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: SorterResult<any>['field'];
    sortOrder?: SorterResult<any>['order'];
}

const getParams = (params: TableParams) => ({
    results: params.pagination?.pageSize,
    page: params.pagination?.current,
    ...params,
});

const ENCRYPTED_WALLET_LABEL = 'Encrypted';

const Coins: React.FC = () => {
    const history = useNavigate();
    const { logout } = useAuth();
    const axiosInstance = useMemo(() => createAxiosInstance(history, logout), [history, logout]);
    const { selectedServer, loading: configLoading } = useServerContext();

    const [search, setSearch] = useState('');
    const [data, setData] = useState<DataType[]>();
    const [loading, setLoading] = useState(false);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: { current: 1, pageSize: 10 },
    });

    const highlight = (text: unknown) => {
        const value = text?.toString() ?? '';
        if (!search.trim()) return value;
        return (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[search.trim()]}
                autoEscape
                textToHighlight={value}
            />
        );
    };

    const renderEllipsis = (content: React.ReactNode, title?: string) => (
        <span className="coins-table-ellipsis" title={title}>
            {content}
        </span>
    );

    const renderExternalLink = (value: unknown, getHref: (text: string) => string) => {
        const text = value?.toString()?.trim() ?? '';
        if (!text) return '—';
        return renderEllipsis(
            <a
                className="coins-table-link"
                href={getHref(text)}
                target="_blank"
                rel="noopener noreferrer"
            >
                {highlight(text)}
            </a>,
            text
        );
    };

    const columns: ColumnsType<DataType> = [
        { title: 'No', dataIndex: 'index', sorter: true, width: '5%', render: highlight },
        { title: 'Token Symbol', dataIndex: 'token_symbol', width: '9%', sorter: true, ellipsis: true, render: (value, record) => highlight(resolveTokenLabel(value?.toString(), undefined, record.token_address)) },
        {
            title: 'User',
            dataIndex: 'username',
            width: '9%',
            sorter: true,
            ellipsis: true,
            render: (value: unknown) => {
                const text = value?.toString()?.trim() ?? '';
                return text ? renderEllipsis(highlight(text), text) : '—';
            },
        },
        {
            title: 'Token Address',
            dataIndex: 'token_address',
            width: '14%',
            sorter: true,
            ellipsis: true,
            render: (value: unknown) => renderExternalLink(value, (text) => `https://solscan.io/token/${text}`),
        },
        {
            title: 'Dex',
            dataIndex: 'dex',
            width: '10%',
            sorter: true,
            ellipsis: true,
            render: (_: unknown, record: DataType) => {
                const dexPool = [record.dex, record.pool_type].filter(Boolean).join('-');
                return highlight(dexPool);
            },
        },
        {
            title: 'Pool Id',
            dataIndex: 'pool_id',
            width: '15%',
            sorter: true,
            ellipsis: true,
            render: (value: unknown) => renderExternalLink(value, (text) => `https://dexscreener.com/solana/${text}`),
        },
        {
            title: 'Wallet (pubkey)',
            dataIndex: 'wallet',
            width: '12%',
            ellipsis: true,
            render: (value: unknown) => {
                const text = value?.toString()?.trim() ?? '';
                if (!text) return '—';
                if (text === ENCRYPTED_WALLET_LABEL) {
                    return renderEllipsis(highlight(text), text);
                }
                return renderExternalLink(text, (t) => `https://solscan.io/account/${t}`);
            },
        },
    ];

    const fetchData = async () => {
        if (!selectedServer) return;
        setLoading(true);
        try {
            const results = await axiosInstance.post('/api/v1/get_server_tokens', {
                serverId: selectedServer.serverId,
                search: search.trim() || undefined,
                ...getParams(tableParams),
            });
            if (results.data?.flag) {
                setData(results.data.tokens);
                setTableParams({
                    ...tableParams,
                    pagination: { ...tableParams.pagination, total: results.data.totalNum },
                });
            } else {
                notifyError(results.data?.message);
            }
        } catch {
            notifyError('Failed to fetch tokens');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedServer) fetchData();
    }, [
        selectedServer?.serverId,
        tableParams.pagination?.current,
        tableParams.pagination?.pageSize,
        tableParams?.sortOrder,
        tableParams?.sortField,
        search,
    ]);

    const handleTableChange: TableProps<DataType>['onChange'] = (pagination, _filters, sorter) => {
        setTableParams({
            pagination,
            sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
            sortField: Array.isArray(sorter) ? undefined : sorter.field,
        });
        if (pagination.pageSize !== tableParams.pagination?.pageSize) {
            setData([]);
        }
    };

    if (configLoading) {
        return (
            <WithAuth><MainWrapper><Spin /></MainWrapper></WithAuth>
        );
    }

    if (!selectedServer) {
        return (
            <WithAuth><MainWrapper><Empty description="No server selected" /></MainWrapper></WithAuth>
        );
    }

    return (
        <WithAuth>
            <MainWrapper>
                <Input
                    placeholder="Search token symbol, user, address, dex, pool id..."
                    prefix={<SearchOutlined />}
                    allowClear
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setTableParams((prev) => ({
                            ...prev,
                            pagination: { ...prev.pagination, current: 1 },
                        }));
                    }}
                    style={{ marginBottom: 16, maxWidth: 480 }}
                />
                <Table
                    className="dashboard-data-table coins-table"
                    columns={columns}
                    rowKey={(record) => record._id}
                    dataSource={data}
                    pagination={tableParams.pagination}
                    loading={loading}
                    onChange={handleTableChange}
                    bordered
                    size="middle"
                    tableLayout="fixed"
                />
            </MainWrapper>
        </WithAuth>
    );
};

export default Coins;
